/**
 * Per-`tableType` Manage Columns behavior policy.
 *
 * The shared columns reducer is reused by many product surfaces (the same table
 * component renders both Vacancy Analysis and the Property View drill-downs, only
 * differing by `tableType`). Order-persistence must therefore be scoped by
 * `tableType`, NOT by component or route.
 *
 * Safe default = LEGACY behavior (do not preserve user order). A table only
 * preserves order if it explicitly opts in here. Today only Vacancy Analysis
 * (`vacancy_analysis_full`) is approved.
 *
 * - preserveStaticOrder: keep the user's drag order of static columns across remount.
 * - preserveCustomOrder:  keep a custom column's interleaved position across reload.
 */

// Frozen so the shared default object can never be accidentally mutated by callers.
export const DEFAULT_COLUMN_POLICY = Object.freeze({
  preserveStaticOrder: false,
  preserveCustomOrder: false,
});

const COLUMN_POLICIES = Object.freeze({
  // Standalone Vacancy Analysis.
  vacancy_analysis_full: Object.freeze({
    preserveStaticOrder: true,
    preserveCustomOrder: true,
  }),
  // Portfolio → Property View → Vacancy Analysis. A SEPARATE key from
  // vacancy_analysis_full (so the two surfaces stay isolated) that also preserves
  // its own order/custom placement. Do not map this back to vacancy_analysis_full.
  vacancy_analysis: Object.freeze({
    preserveStaticOrder: true,
    preserveCustomOrder: true,
  }),
  // Lease Renewal Analysis page (LeaseUpAnalysis → LeaseAnalysisChart).
  lease_renewal_analysis: Object.freeze({
    preserveStaticOrder: true,
    preserveCustomOrder: true,
  }),
  // Portfolio → Property View → Lease Expiry Report (DrilldownPropertyView →
  // LeaseExpiringReportChart). Distinct key from lease_renewal_analysis — the two
  // lease surfaces stay isolated.
  drilldown_lease: Object.freeze({
    preserveStaticOrder: true,
    preserveCustomOrder: true,
  }),
  // Work Order Labor Summary — standalone page (WorkOrderSummary.jsx).
  work_order_labor_summary: Object.freeze({
    preserveStaticOrder: true,
    preserveCustomOrder: true,
  }),
  // Work Order Labor Summary — Investor Report section (WorkOrderReportSection.jsx).
  // Separate key so investor column prefs stay independent from the standalone page.
  work_order_labor_summary_investor: Object.freeze({
    preserveStaticOrder: true,
    preserveCustomOrder: true,
  }),
  // Delinquency Analysis (DelinquencyAnalysis → PropertyReportTable).
  delinquency_analysis: Object.freeze({
    preserveStaticOrder: true,
    preserveCustomOrder: true,
  }),
  // Owner Communication page (/pm-communication → ManagerReview → PropertyReportTable).
  // Order/custom placement persist; manager_review's "custom columns hidden by default"
  // behavior is enforced separately in the reducer's visibility step and is unaffected.
  manager_review: Object.freeze({
    preserveStaticOrder: true,
    preserveCustomOrder: true,
  }),
  // Investor Report → Current Rent Roll table (OwnerReport → UnitDetailsTable).
  unit_details: Object.freeze({
    preserveStaticOrder: true,
    preserveCustomOrder: true,
  }),
});

export const getColumnPolicy = (tableType) =>
  COLUMN_POLICIES[tableType] || DEFAULT_COLUMN_POLICY;
